import {
  getMapboxStatistics,
  getRoutingStatistics,
  getStudentStatistics,
  getVehicleStatistics,
  getFuelPricesStatistics,
  getAttendanceStatistics,
} from '@/services/api/analytics/analytics'
import {
  getSafetySummary,
  getSafetyReport,
  getSafetyVehicles,
  getSafetyEvents,
  getSafetyScoreDistribution,
} from '@/services/api/analytics/augustus'

export default {
  namespaced: true,
  state: {
    routingStatistics: null,
    navigationStatistics: [],
    mapsStatistics: [],
    studentStatistics: null,
    studentStatisticsMetaData: null,
    vehicleStatistics: null,
    vehicleStatisticsMetaData: null,
    fuelPricesStatistics: null,
    attendanceStats: null,
    safetySummary: null,
    safetyReport: null,
    safetyVehicles: null,
    safetyEvents: null,
    safetyScoreDistribution: null,
    safetyLoading: false,
    loading: false,
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ROUTING_STATISTICS(state, payload) {
      state.routingStatistics = payload
    },
    SET_NAVIGATION_STATISTICS(state, payload) {
      state.navigationStatistics = payload
    },
    SET_MAPS_STATISTICS(state, payload) {
      state.mapsStatistics = payload
    },
    SET_STUDENT_STATISTICS(state, payload) {
      state.studentStatistics = payload
    },
    SET_STUDENT_STATISTICS_META_DATA(state, payload) {
      state.studentStatisticsMetaData = payload
    },
    SET_VEHICLE_STATISTICS(state, payload) {
      state.vehicleStatistics = payload
    },
    SET_VEHICLE_STATISTICS_META_DATA(state, payload) {
      state.vehicleStatisticsMetaData = payload
    },
    SET_FUEL_PRICES_STATISTICS(state, payload) {
      state.fuelPricesStatistics = payload
    },
    SET_ATTENDANCE_STATS(state, payload) {
      state.attendanceStats = payload
    },
    SET_SAFETY_SUMMARY(state, payload) {
      state.safetySummary = payload
    },
    SET_SAFETY_REPORT(state, payload) {
      state.safetyReport = payload
    },
    SET_SAFETY_VEHICLES(state, payload) {
      state.safetyVehicles = payload
    },
    SET_SAFETY_EVENTS(state, payload) {
      state.safetyEvents = payload
    },
    SET_SAFETY_SCORE_DISTRIBUTION(state, payload) {
      state.safetyScoreDistribution = payload
    },
    SET_SAFETY_LOADING(state, loading) {
      state.safetyLoading = loading
    },
  },
  actions: {
    async getRoutingStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      const response = await getRoutingStatistics(payload)
      const chartData = calculateRoutingStatisticData(response.data)
      commit('SET_ROUTING_STATISTICS', chartData)
      commit('SET_LOADING', false)
    },
    async getMapboxStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      const response = await getMapboxStatistics(payload)
      const chartData = calculateMapboxStatisticData(response.data)
      commit('SET_NAVIGATION_STATISTICS', chartData.navigationStatistics)
      commit('SET_MAPS_STATISTICS', chartData.mapsStatistics)
      commit('SET_LOADING', false)
    },
    async getStudentStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      const response = await getStudentStatistics(payload)
      const chartData = calculateStudentStatisticData(
        response.data.studentStatistics,
      )
      commit('SET_STUDENT_STATISTICS', chartData)
      commit('SET_STUDENT_STATISTICS_META_DATA', response.data.metadata)
      commit('SET_LOADING', false)
    },
    async getVehicleStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      const response = await getVehicleStatistics(payload)
      const chartData = calculateVehicleStatisticData(
        response.data.vehicleStatistics,
      )
      commit('SET_VEHICLE_STATISTICS', chartData)
      commit('SET_VEHICLE_STATISTICS_META_DATA', [response.data.metadata])
      commit('SET_LOADING', false)
    },
    async getFuelPricesStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      const response = await getFuelPricesStatistics(payload)
      const chartData = calculateFuelPricesStatisticData(response.data)
      commit('SET_FUEL_PRICES_STATISTICS', chartData)
      commit('SET_LOADING', false)
    },
    async getAttendanceStatistics({ commit }, payload) {
      commit('SET_LOADING', true)
      try {
        const response = await getAttendanceStatistics(payload)
        if (response.data && response.data.timeSeries) {
          commit('SET_ATTENDANCE_STATS', response.data)
        } else {
          console.warn('No attendance data returned, using mock data')
          commit('SET_ATTENDANCE_STATS', generateMockAttendanceStats(payload))
        }
      } catch (error) {
        console.error('Error fetching attendance statistics, using mock data:', error)
        commit('SET_ATTENDANCE_STATS', generateMockAttendanceStats(payload))
      }
      commit('SET_LOADING', false)
    },
    async fetchSafetySummary({ commit }, { from, to }) {
      try {
        const response = await getSafetySummary({ from, to })
        commit('SET_SAFETY_SUMMARY', response.data)
      } catch (error) {
        console.error('Error fetching safety summary:', error)
      }
    },
    async fetchSafetyReport({ commit }, { from, to }) {
      try {
        const response = await getSafetyReport({ from, to })
        commit('SET_SAFETY_REPORT', response.data)
      } catch (error) {
        console.error('Error fetching safety report:', error)
      }
    },
    async fetchSafetyVehicles({ commit }, { from, to }) {
      try {
        const response = await getSafetyVehicles({ from, to })
        commit('SET_SAFETY_VEHICLES', response.data)
      } catch (error) {
        console.error('Error fetching safety vehicles:', error)
      }
    },
    async fetchSafetyEvents({ commit }, { from, to, limit, offset }) {
      try {
        const response = await getSafetyEvents({ from, to, limit, offset })
        commit('SET_SAFETY_EVENTS', response.data)
      } catch (error) {
        console.error('Error fetching safety events:', error)
      }
    },
    async fetchSafetyScoreDistribution({ commit }, { from, to }) {
      try {
        const response = await getSafetyScoreDistribution({ from, to })
        commit('SET_SAFETY_SCORE_DISTRIBUTION', response.data)
      } catch (error) {
        console.error('Error fetching safety score distribution:', error)
        commit('SET_SAFETY_SCORE_DISTRIBUTION', null)
      }
    },
    async fetchAllSafetyData({ dispatch, commit }, { from, to }) {
      commit('SET_SAFETY_LOADING', true)
      try {
        await Promise.all([
          dispatch('fetchSafetySummary', { from, to }),
          dispatch('fetchSafetyReport', { from, to }),
          dispatch('fetchSafetyVehicles', { from, to }),
          dispatch('fetchSafetyEvents', { from, to, limit: 50, offset: 0 }),
          dispatch('fetchSafetyScoreDistribution', { from, to }),
        ])
      } finally {
        commit('SET_SAFETY_LOADING', false)
      }
    },
  },
  getters: {},
}

function calculateRoutingStatisticData(routingStatistics) {
  let xValues = ['x']
  let totalRoutesData = {
    groupTitle: 'General',
    title: 'Total Routes',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'routes',
  }

  let totalRouteLengthData = {
    groupTitle: 'General',

    title: 'Total Route Length',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'hours',
  }

  let avgRouteLengthData = {
    groupTitle: 'General',

    title: 'Avg. Route Length',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'mins',
  }

  let totalRoutingDistanceData = {
    groupTitle: 'General',

    title: 'Total Distance',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'miles',
  }

  let avgRoutingDistanceData = {
    groupTitle: 'General',

    title: 'Avg. Route Distance',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'miles',
  }

  let totalRidershipData = {
    groupTitle: 'General',

    title: 'Total Ridership',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'students',
  }

  let avgRidershipData = {
    groupTitle: 'General',

    title: 'Avg. Ridership',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'students',
  }

  let totalStopCountData = {
    groupTitle: 'General',

    title: 'Total Stops Count',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'stops',
  }

  let avgStopCountData = {
    groupTitle: 'General',

    title: 'Avg. Stops Count',
    xValues: ['x'],
    yValues: [['Fixed Routes'], ['Specialized']],
    total: 0,
    unit: 'stops',
  }

  for (let i = 0; i < routingStatistics.length; i++) {
    const dayStas = routingStatistics[i]
    xValues.push(dayStas.date)

    totalRoutesData.yValues[0].push(dayStas.routingCount)
    totalRoutesData.yValues[1].push(dayStas.specializedRoutingCount)

    totalRouteLengthData.yValues[0].push(
      (dayStas.totalRoutingsLength / 60).toFixed(2),
    )
    totalRouteLengthData.yValues[1].push(
      (dayStas.totalSpecializedRoutingsLength / 60).toFixed(2),
    )

    avgRouteLengthData.yValues[0].push(dayStas.avgRoutingsLength.toFixed(2))
    avgRouteLengthData.yValues[1].push(
      dayStas.avgSpecializedRoutingsLength.toFixed(2),
    )

    totalRoutingDistanceData.yValues[0].push(
      (dayStas.totalRoutingDistance * 0.000621371192).toFixed(2),
    )
    totalRoutingDistanceData.yValues[1].push(
      (dayStas.totalSpecializedRoutingDistance * 0.000621371192).toFixed(2),
    )

    avgRoutingDistanceData.yValues[0].push(
      (dayStas.avgRoutingDistance * 0.000621371192).toFixed(2),
    )
    avgRoutingDistanceData.yValues[1].push(
      (dayStas.avgSpecializedRoutingDistance * 0.000621371192).toFixed(2),
    )

    totalRidershipData.yValues[0].push(dayStas.routingStudents)
    totalRidershipData.yValues[1].push(dayStas.specializedRoutingStudents)

    avgRidershipData.yValues[0].push(dayStas.avgRoutingStudents.toFixed(2))
    avgRidershipData.yValues[1].push(
      dayStas.avgSpecializedRoutingStudents.toFixed(2),
    )

    totalStopCountData.yValues[0].push(dayStas.routingStopCount)
    totalStopCountData.yValues[1].push(dayStas.specializedRoutingStopCount)

    avgStopCountData.yValues[0].push(dayStas.avgRoutingStopCount.toFixed(2))
    avgStopCountData.yValues[1].push(
      dayStas.avgSpecializedRoutingStopCount.toFixed(2),
    )

    if (i === routingStatistics.length - 1) {
      totalRoutesData.total =
        dayStas.routingCount + dayStas.specializedRoutingCount
      totalRouteLengthData.total = (
        (dayStas.totalRoutingsLength + dayStas.totalSpecializedRoutingsLength) /
        60
      ).toFixed(2)
      avgRouteLengthData.total = (
        (dayStas.avgRoutingsLength + dayStas.avgSpecializedRoutingsLength) /
        2
      ).toFixed(2)
      totalRoutingDistanceData.total = (
        (dayStas.totalRoutingDistance +
          dayStas.totalSpecializedRoutingDistance) *
        0.000621371192
      ).toFixed(2)
      avgRoutingDistanceData.total = (
        ((dayStas.avgRoutingDistance + dayStas.avgSpecializedRoutingDistance) *
          0.000621371192) /
        2
      ).toFixed(2)
      totalRidershipData.total =
        dayStas.routingStudents + dayStas.specializedRoutingStudents
      avgRidershipData.total = (
        (dayStas.avgRoutingStudents + dayStas.avgSpecializedRoutingStudents) /
        2
      ).toFixed(2)
      totalStopCountData.total =
        dayStas.routingStopCount + dayStas.specializedRoutingStopCount
      avgStopCountData.total = (
        (dayStas.avgRoutingStopCount + dayStas.avgSpecializedRoutingStopCount) /
        2
      ).toFixed(2)
    }
  }

  totalRoutesData.xValues = xValues
  totalRouteLengthData.xValues = xValues
  avgRouteLengthData.xValues = xValues
  totalRoutingDistanceData.xValues = xValues
  avgRoutingDistanceData.xValues = xValues
  avgRidershipData.xValues = xValues
  totalRidershipData.xValues = xValues
  avgStopCountData.xValues = xValues
  totalStopCountData.xValues = xValues

  return [
    totalRoutesData,
    totalRoutingDistanceData,
    avgRoutingDistanceData,
    totalRouteLengthData,
    avgRouteLengthData,
    totalRidershipData,
    avgRidershipData,
    totalStopCountData,
    avgStopCountData,
  ]
}

function calculateMapboxStatisticData(statistics) {
  let xValues = ['x']
  let totalDirectionsAPIData = {
    groupTitle: 'Navigation',
    title: 'Directions',
    xValues: ['x'],
    yValues: [['Directions API']],
    total: 0,
    unit: 'requests',
  }

  let totalMapLoadsForWebData = {
    groupTitle: 'Maps',
    title: 'Map Loads for Web',
    xValues: ['x'],
    yValues: [['Map Loads']],
    total: 0,
    unit: 'loads',
  }

  for (let i = 0; i < statistics.directionsAPIStatisticsData.length; i++) {
    const dayStas = statistics.directionsAPIStatisticsData[i]
    xValues.push(dayStas.date)

    totalDirectionsAPIData.yValues[0].push(dayStas.count)
    totalDirectionsAPIData.total += dayStas.count
  }

  for (let i = 0; i < statistics.mapLoadsForWebStatisticsData.length; i++) {
    const dayStas = statistics.mapLoadsForWebStatisticsData[i]

    totalMapLoadsForWebData.yValues[0].push(dayStas.count)
    totalMapLoadsForWebData.total += dayStas.count
  }

  totalDirectionsAPIData.xValues = xValues
  totalMapLoadsForWebData.xValues = xValues

  return {
    navigationStatistics: [totalDirectionsAPIData],
    mapsStatistics: [totalMapLoadsForWebData],
  }
}

function calculateStudentStatisticData(data) {
  let xValues = ['x']
  let totalStudentData = {
    groupTitle: 'General',
    title: 'Total Students',
    xValues: ['x'],
    yValues: [['Students']],
    total: 0,
    unit: 'students',
  }

  for (let i = 0; i < data.length; i++) {
    const dayStas = data[i]
    xValues.push(dayStas.date)

    totalStudentData.yValues[0].push(dayStas.totalStudents)

    if (i === data.length - 1) {
      totalStudentData.total = dayStas.totalStudents
    }
  }

  totalStudentData.xValues = xValues

  return [totalStudentData]
}

function calculateVehicleStatisticData(data) {
  let xValues = ['x']
  let totalVehiclesData = {
    groupTitle: 'General',
    title: 'Total Vehicles',
    xValues: ['x'],
    yValues: [['Vehicles']],
    total: 0,
    unit: 'vehicles',
  }
  let avgVehicleAgeData = {
    groupTitle: 'General',
    title: 'Average Vehicle Age',
    xValues: ['x'],
    yValues: [['Vehicles']],
    total: 0,
    unit: 'years',
  }

  for (let i = 0; i < data.length; i++) {
    const dayStas = data[i]
    xValues.push(dayStas.date)

    totalVehiclesData.yValues[0].push(dayStas.totalVehicles)

    if (i === data.length - 1) {
      totalVehiclesData.total = dayStas.totalVehicles
    }

    avgVehicleAgeData.yValues[0].push(dayStas.avgVehicleAge.toFixed(2))

    if (i === data.length - 1) {
      avgVehicleAgeData.total = dayStas.avgVehicleAge.toFixed(2)
    }
  }

  totalVehiclesData.xValues = xValues
  avgVehicleAgeData.xValues = xValues

  return [totalVehiclesData, avgVehicleAgeData]
}

function calculateFuelPricesStatisticData(data) {
  let xValues = ['x']
  let gasolinePricesData = {
    groupTitle: 'Fuel',
    title: 'Reg. Gasoline',
    xValues: ['x'],
    yValues: [['Gasoline']],
    total: 0,
    unit: 'per gallon',
    metadata: [
      {
        name: 'State',
        data: data.gasolinePricesData.metadata,
      },
    ],
  }

  for (let i = 0; i < data.gasolinePricesData.data.length; i++) {
    const dayStas = data.gasolinePricesData.data[i]
    xValues.push(dayStas.date)

    gasolinePricesData.yValues[0].push(dayStas.price.toFixed(2))

    if (i === data.gasolinePricesData.data.length - 1) {
      gasolinePricesData.total = dayStas.price.toFixed(2)
    }
  }

  gasolinePricesData.xValues = xValues

  let dieselPricesData = {
    groupTitle: 'Fuel',
    title: 'Diesel',
    xValues: ['x'],
    yValues: [['Diesel']],
    total: 0,
    unit: 'per gallon',
    metadata: [
      {
        name: 'State',
        data: data.dieselPricesData.metadata,
      },
    ],
  }

  for (let i = 0; i < data.dieselPricesData.data.length; i++) {
    const dayStas = data.dieselPricesData.data[i]
    xValues.push(dayStas.date)

    dieselPricesData.yValues[0].push(dayStas.price.toFixed(2))

    if (i === data.dieselPricesData.data.length - 1) {
      dieselPricesData.total = dayStas.price.toFixed(2)
    }
  }

  dieselPricesData.xValues = xValues

  return [gasolinePricesData, dieselPricesData]
}

function generateMockAttendanceStats(payload) {
  // Generate date labels based on payload range
  const startDate = payload?.startDate ? new Date(payload.startDate) : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  const endDate = payload?.endDate ? new Date(payload.endDate) : new Date()
  const labels = []
  const currentDate = new Date(startDate)

  while (currentDate <= endDate) {
    const month = currentDate.toLocaleString('en-US', { month: 'short' })
    const day = currentDate.getDate()
    labels.push(`${month} ${day}`)
    currentDate.setDate(currentDate.getDate() + 1)
  }

  // Generate random values for time series
  const generateValues = (count, min, max) => {
    return Array.from({ length: count }, () => Math.floor(Math.random() * (max - min + 1)) + min)
  }

  const dayCount = labels.length

  // Helper to generate 24-hour heatmap data
  const generateHeatmap = () => {
    return Array.from({ length: 24 }, (_, hour) => {
      // Peak hours 7-9am and 12-2pm have higher values
      const isPeak = (hour >= 7 && hour <= 9) || (hour >= 12 && hour <= 14)
      const isLow = hour >= 22 || hour <= 4
      const baseMin = isLow ? 1 : isPeak ? 40 : 15
      const baseMax = isLow ? 15 : isPeak ? 95 : 60
      return Array.from({ length: 7 }, () => Math.floor(Math.random() * (baseMax - baseMin + 1)) + baseMin)
    })
  }

  return {
    timeSeries: {
      labels,
      // Main chart metrics
      absentScans: generateValues(dayCount, 10, 60),
      duplicateScans: generateValues(dayCount, 5, 40),
      earlyScans: generateValues(dayCount, 100, 400),
      failedScans: generateValues(dayCount, 20, 150),
      lateScans: generateValues(dayCount, 50, 300),
      onTimeScans: generateValues(dayCount, 600, 1800),
      successfulScans: generateValues(dayCount, 700, 2000),
      supervisorOverride: generateValues(dayCount, 5, 30),
      totalBadgeHolder: generateValues(dayCount, 200, 350),
      totalEmployees: generateValues(dayCount, 180, 320),
      totalScans: generateValues(dayCount, 800, 2200),
      totalRfid: generateValues(dayCount, 150, 280),
    },
    dayOfWeek: {
      // Day breakdown metrics
      absentScans: [
        { label: 'Sunday', percent: 1.8 },
        { label: 'Monday', percent: 18.5 },
        { label: 'Tuesday', percent: 20.2 },
        { label: 'Wednesday', percent: 21.5 },
        { label: 'Thursday', percent: 19.8 },
        { label: 'Friday', percent: 15.7 },
        { label: 'Saturday', percent: 2.5 },
      ],
      duplicateScans: [
        { label: 'Sunday', percent: 1.5 },
        { label: 'Monday', percent: 21.2 },
        { label: 'Tuesday', percent: 19.5 },
        { label: 'Wednesday', percent: 20.8 },
        { label: 'Thursday', percent: 18.5 },
        { label: 'Friday', percent: 16.0 },
        { label: 'Saturday', percent: 2.5 },
      ],
      earlyScans: [
        { label: 'Sunday', percent: 2.8 },
        { label: 'Monday', percent: 16.5 },
        { label: 'Tuesday', percent: 21.2 },
        { label: 'Wednesday', percent: 22.5 },
        { label: 'Thursday', percent: 18.5 },
        { label: 'Friday', percent: 15.0 },
        { label: 'Saturday', percent: 3.5 },
      ],
      failedScans: [
        { label: 'Sunday', percent: 1.2 },
        { label: 'Monday', percent: 22.5 },
        { label: 'Tuesday', percent: 18.8 },
        { label: 'Wednesday', percent: 19.5 },
        { label: 'Thursday', percent: 20.2 },
        { label: 'Friday', percent: 15.3 },
        { label: 'Saturday', percent: 2.5 },
      ],
      lateScans: [
        { label: 'Sunday', percent: 3.1 },
        { label: 'Monday', percent: 25.2 },
        { label: 'Tuesday', percent: 17.5 },
        { label: 'Wednesday', percent: 18.8 },
        { label: 'Thursday', percent: 19.2 },
        { label: 'Friday', percent: 13.7 },
        { label: 'Saturday', percent: 2.5 },
      ],
      onTimeScans: [
        { label: 'Sunday', percent: 2.5 },
        { label: 'Monday', percent: 18.2 },
        { label: 'Tuesday', percent: 19.5 },
        { label: 'Wednesday', percent: 20.1 },
        { label: 'Thursday', percent: 18.8 },
        { label: 'Friday', percent: 17.4 },
        { label: 'Saturday', percent: 3.5 },
      ],
      successfulScans: [
        { label: 'Sunday', percent: 2.3 },
        { label: 'Monday', percent: 18.5 },
        { label: 'Tuesday', percent: 19.8 },
        { label: 'Wednesday', percent: 20.5 },
        { label: 'Thursday', percent: 18.2 },
        { label: 'Friday', percent: 17.2 },
        { label: 'Saturday', percent: 3.5 },
      ],
      supervisorOverride: [
        { label: 'Sunday', percent: 1.5 },
        { label: 'Monday', percent: 20.5 },
        { label: 'Tuesday', percent: 18.2 },
        { label: 'Wednesday', percent: 21.3 },
        { label: 'Thursday', percent: 19.8 },
        { label: 'Friday', percent: 16.2 },
        { label: 'Saturday', percent: 2.5 },
      ],
      totalScans: [
        { label: 'Sunday', percent: 2.5 },
        { label: 'Monday', percent: 18.2 },
        { label: 'Tuesday', percent: 19.5 },
        { label: 'Wednesday', percent: 20.1 },
        { label: 'Thursday', percent: 18.8 },
        { label: 'Friday', percent: 17.4 },
        { label: 'Saturday', percent: 3.5 },
      ],
    },
    breakdowns: {
      // Pie chart categories
      compliance: [
        { label: 'On Time', percent: 72.5 },
        { label: 'Early', percent: 15.3 },
        { label: 'Late', percent: 8.7 },
        { label: 'No Scan', percent: 3.5 },
      ],
      depot: [
        { label: 'Main Depot', percent: 45.2 },
        { label: 'North Depot', percent: 28.5 },
        { label: 'South Depot', percent: 16.3 },
        { label: 'West Hub', percent: 10.0 },
      ],
      device: [
        { label: 'Kiosk', percent: 65.5 },
        { label: 'Mobile', percent: 25.3 },
        { label: 'RFID Reader', percent: 9.2 },
      ],
      gateway: [
        { label: 'Gateway A', percent: 42.5 },
        { label: 'Gateway B', percent: 35.2 },
        { label: 'Gateway C', percent: 22.3 },
      ],
      gender: [
        { label: 'Male', percent: 52.3 },
        { label: 'Female', percent: 46.5 },
        { label: 'Other', percent: 1.2 },
      ],
      kiosks: [
        { label: 'Kiosk 1', percent: 45.5 },
        { label: 'Kiosk 2', percent: 32.3 },
        { label: 'Kiosk 3', percent: 15.7 },
        { label: 'Kiosk 4', percent: 6.5 },
      ],
      role: [
        { label: 'Driver', percent: 55.2 },
        { label: 'Attendant', percent: 28.5 },
        { label: 'Supervisor', percent: 10.3 },
        { label: 'Other', percent: 6.0 },
      ],
      shift: [
        { label: 'AM Shift', percent: 52.5 },
        { label: 'PM Shift', percent: 35.2 },
        { label: 'Night Shift', percent: 12.3 },
      ],
    },
    heatmap: {
      // Heatmap metrics (keyed by metric name)
      compliance: generateHeatmap(),
      depot: generateHeatmap(),
      duplicateScans: generateHeatmap(),
      failedScans: generateHeatmap(),
      gateway: generateHeatmap(),
      lateScans: generateHeatmap(),
      shift: generateHeatmap(),
      successfulScans: generateHeatmap(),
      supervisorOverride: generateHeatmap(),
      totalScans: generateHeatmap(),
    },
    scorecard: {
      totalScansPercent: '19.5',
      avgLateness: '4.2',
      complianceRate: '87.5',
      noShowRate: '3.5',
    },
  }
}
